import { Mesh } from 'three';

/**
 * Circular linked list implementation for Mesh objects.
 * Provides efficient splicing for continuous drawing.
 */
export class ListNode<T> {
  value: T;
  next: ListNode<T>;
  constructor(value: T) {
    this.value = value;
    // Initially, a node points to itself, preserving the circular structure.
    this.next = this;
  }

  dispose(): void {
    const value = this.value as Mesh & { destroy?: () => void };
    if (typeof value.destroy === 'function') {
      value.destroy();
      return;
    }
    if (value instanceof Mesh) {
      value.geometry.dispose();
      if (value.parent) {
        value.parent.remove(value);
      }
    }
  }
}

export class CircularLinkedList<T> {
  head: ListNode<T> | null = null;
  tail: ListNode<T> | null = null;
  size: number = 0;

  add(value: T): ListNode<T> {
    const newNode = new ListNode(value);
    if (!this.head) {
      this.head = newNode;
      this.tail = newNode;
      newNode.next = newNode;
    } else {
      newNode.next = this.head;
      if (this.tail) {
        this.tail.next = newNode;
      }
      this.tail = newNode;
    }
    this.size++;
    return newNode;
  }

  /**
   * Inserts a new node at the specified index.
   */
  insertAt(index: number, value: T): ListNode<T> {
    if (index < 0 || index > this.size) {
      throw new Error('Index out of bounds');
    }
    const newNode = new ListNode(value);
    if (index === 0) {
      if (!this.head) {
        this.head = newNode;
        this.tail = newNode;
        newNode.next = newNode;
      } else {
        newNode.next = this.head;
        this.head = newNode;
        if (this.tail) {
          this.tail.next = this.head;
        }
      }
    } else {
      let current = this.head!;
      for (let i = 0; i < index - 1; i++) {
        current = current.next;
      }
      newNode.next = current.next;
      current.next = newNode;
      if (current === this.tail) {
        this.tail = newNode;
      }
    }
    this.size++;
    return newNode;
  }

  /**
   * Inserts a new node immediately after the given node.
   */
  insertAfter(node: ListNode<T>, value: T): ListNode<T> {
    const newNode = new ListNode(value);
    newNode.next = node.next;
    node.next = newNode;
    if (node === this.tail) {
      this.tail = newNode;
    }
    this.size++;
    return newNode;
  }

  /**
   * Returns the index of the first node whose value equals `value`, or -1.
   */
  indexOf(value: T): number {
    if (!this.head) return -1;
    let current = this.head;
    let index = 0;
    do {
      if (current.value === value) return index;
      current = current.next;
      index++;
    } while (current !== this.head && index < this.size);
    return -1;
  }

  /**
   * Returns the node at the given index.
   */
  getNodeAt(index: number): ListNode<T> | null {
    if (index < 0 || index >= this.size || !this.head) return null;
    let current = this.head;
    for (let i = 0; i < index; i++) {
      current = current.next;
    }
    return current;
  }

  /**
   * Replaces all nodes between the node at startIndex and the node at endIndex
   * with new nodes constructed from newValues, disposing the replaced nodes.
   *
   * @param startIndex - The index of the start node.
   * @param endIndex - The index of the end node.
   * @param newValues - An array of values to insert between the start and end nodes.
   *
   * @throws Error if either index is out of bounds.
   */
  replaceBetween(startIndex: number, endIndex: number, newValues: T[]): void {
    if (this.size < 2) return;

    if (startIndex < 0 || startIndex >= this.size || endIndex < 0 || endIndex >= this.size) {
      throw new Error('Index out of bounds');
    }

    // Obtain the start and end nodes.
    const startNode = this.getNodeAt(startIndex)!;
    const endNode = this.getNodeAt(endIndex)!;

    // Collect all nodes between startNode.next (inclusive) and endNode (exclusive)
    const nodesToDispose: ListNode<T>[] = [];
    let current = startNode.next;
    while (current !== endNode) {
      nodesToDispose.push(current);
      current = current.next;
      // Safeguard: in case of a corrupted list.
      if (current === startNode) break;
    }
    const removedCount = nodesToDispose.length;

    // Create a new chain of nodes from newValues.
    let newChainHead: ListNode<T> | null = null;
    let newChainTail: ListNode<T> | null = null;
    for (const val of newValues) {
      const newNode = new ListNode(val);
      if (newChainHead === null) {
        newChainHead = newNode;
        newChainTail = newNode;
      } else {
        newChainTail!.next = newNode;
        newChainTail = newNode;
      }
    }

    // Dispose of each node that is being replaced.
    for (const node of nodesToDispose) {
      node.dispose();
    }

    // Reconnect the nodes by splicing the new chain in place of the removed nodes.
    if (newChainHead !== null && newChainTail !== null) {
      startNode.next = newChainHead;
      newChainTail.next = endNode;
    } else {
      // If no new nodes provided, simply bypass the removed segment.
      startNode.next = endNode;
    }

    // If the removed segment contained the current head, update the head.
    // (We check if the original head is among the disposed nodes.)
    if (nodesToDispose.indexOf(this.head!) !== -1) {
      this.head = newChainHead !== null ? newChainHead : endNode;
    }

    // Update the list size.
    this.size = this.size - removedCount + newValues.length;

    // Recalculate the tail by traversing from head over size-1 steps.
    if (this.head) {
      let node = this.head;
      for (let i = 0; i < this.size - 1; i++) {
        node = node.next;
      }
      this.tail = node;
      if (this.size > 0) {
        this.tail.next = this.head;
      }
    }
  }

  /**
   * Converts the circular linked list to an array.
   */
  toArray(): T[] {
    const arr: T[] = [];
    if (!this.head) return arr;
    let current = this.head;
    do {
      arr.push(current.value);
      current = current.next;
    } while (current !== this.head);
    return arr;
  }

  clear(): void {
    if (!this.head) return;
    let current = this.head;
    do {
      current.dispose();
      current = current.next;
    } while (current !== this.head);
    this.head = null;
    this.tail = null;
    this.size = 0;
  }

  forEach(callback: (value: T, index: number) => void): void {
    let index = 0;
    for (const value of this) {
      callback(value, index++);
    }
  }

  /**
   * Enables iteration over the list using for...of.
   * Yields each value once by traversing from head back to head.
   */
  *[Symbol.iterator](): IterableIterator<T> {
    if (!this.head) return;
    let current = this.head;
    do {
      yield current.value;
      current = current.next;
    } while (current !== this.head);
  }
}
